import PropTypes from 'prop-types';
import React, {
  Fragment,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table } from 'grab-ui';
import cx from 'classnames';
import I18n from 'i18n-js';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import ScribeDuck from 'ducks/scribe-duck';
import TrackGADuck from 'ducks/track-ga-duck';
import { getUserCountryCode } from 'helper/user';
import { getAmountDisplay, getDeliveryID, isShopifyOrder } from 'helper/order';
import { isOnGoingOrderCancellable } from 'helper/history-order/history-order';
import {
  ongoingBookingFilterSelector,
  ongoingDeliveriesV2Selector,
} from 'selectors/new-history-selectors';
import {
  showInTransitQRCodeSelector,
  userInfoSelector,
} from 'selectors/app-selector';
import { MSD_STEP_STATE } from 'constants/order-constants';
import MSDTrackingModal from 'components/history/modal/msd-tracking-modal';
import OrderStatus from 'components/v2/order-status/order-status';
import Dropdown from 'components/v2/drop-down/drop-down';
import Driver from 'components/v2/ongoing-bookings-section/table-columns/driver';
import DeliveryStepWithPopup from 'components/v2/ongoing-bookings-section/table-columns/delivery-step';
import { ColumnWithLabel } from 'components/v2/ongoing-bookings-section/table-columns/common';
import PickupTime, {
  pickUpTimeSorter,
} from 'components/v2/ongoing-bookings-section/table-columns/pickup-time';
import NewHistoryDuck from 'ducks/new-history-duck';
import threeDots from 'public/images/three-dots-v2.svg';
import printIcon from 'public/icons/print.svg';
import trackIcon from 'public/icons/trackBooking.svg';
import cancelIcon from 'public/icons/cancel.svg';
import { TooltipContent } from './ongoing-booking-action';
import { useFilteredOngoingOrder, usePrintLabels } from './common-hooks';
import commonTable from './common-table.less';
import styles from './ongoing-booking-section.less';

const BookingCode = ({ order }) => {
  const id = useMemo(() => getDeliveryID(order), [order]);
  const isViaGrabMerchant = useMemo(() => _get(order, 'source') === 2, [order]);

  return (
    <ColumnWithLabel
      label={
        isViaGrabMerchant ? I18n.t('v2.orderTable.column.viaGrabMerchant') : ''
      }
    >
      {id}
    </ColumnWithLabel>
  );
};

BookingCode.propTypes = {
  order: PropTypes.shape({}),
};

BookingCode.defaultProps = {
  order: undefined,
};

const DeliveryStep = ({ isSender, steps, order }) => {
  const componentProps = useMemo(
    () => {
      const data = {};

      if (isSender) {
        const senderSteps = steps ? steps.slice(0, 1) : [];

        data.isPickUp = true;
        data.steps = senderSteps;
      } else {
        const recipientSteps = steps ? steps.slice(1) : [];
        const thirdPartySource = _get(order, 'thirdPartySource', null);
        const isShopify = isShopifyOrder(thirdPartySource);
        const shopifyMetaData = isShopify
          ? _get(order, 'shopifyMetaData', [])
          : [];

        data.steps = recipientSteps;
        data.shopifyMetaDatas = shopifyMetaData;
      }

      return data;
    },
    [isSender, steps, order],
  );

  return <DeliveryStepWithPopup {...componentProps} />;
};

DeliveryStep.propTypes = {
  isSender: PropTypes.bool,
  steps: PropTypes.arrayOf(PropTypes.shape({})),
  order: PropTypes.shape({}),
};

DeliveryStep.defaultProps = {
  isSender: false,
  steps: undefined,
  order: undefined,
};

const OrderActionColumn = ({
  order,
  openMSDTrackingModal,
  startCancellation,
}) => {
  const dispatch = useDispatch();

  const printLabel = usePrintLabels({ orders: [order] });
  const trackDelivery = useCallback(
    () => {
      if (_isEmpty(order)) return;

      const { steps, trackingURL, webTrackingURLsMap } = order;
      const ongoingRecipient = steps
        .slice(1)
        .find(
          step => step.status === MSD_STEP_STATE.Ongoing && step.webTrackingURL,
        );

      if (!_isEmpty(ongoingRecipient)) {
        window.open(ongoingRecipient.webTrackingURL);
      } else if (!_isEmpty(webTrackingURLsMap)) {
        openMSDTrackingModal(webTrackingURLsMap);
      } else if (trackingURL) {
        window.open(trackingURL);
      }
    },
    [order],
  );

  const trackAnalytics = () => {
    if (_isEmpty(order)) return;
    dispatch(
      TrackGADuck.creators.trackGAExpressBookingsWebClick(
        'ONGOING_BOOKINGS_DETAILS',
        { ORDER_ID: order.deliveryID },
      ),
    );
    dispatch(
      ScribeDuck.creators.scribeExpressBookingsWebOngoingTableAction(order),
    );
  };

  const dropdownItems = useMemo(
    () => {
      if (_isEmpty(order)) return [];

      const untrackableOrder =
        _isEmpty(_get(order, 'trackingURL')) &&
        _isEmpty(_get(order, 'webTrackingURLsMap'));
      const orderCancellable = isOnGoingOrderCancellable(order);

      return [
        {
          value: I18n.t('v2.bookingsPage.printLabel'),
          icon: <img src={printIcon} alt="" />,
          onClick: printLabel,
        },
        {
          value: I18n.t('v2.bookingsPage.trackDelivery'),
          icon: <img src={trackIcon} alt="" />,
          onClick: trackDelivery,
          disabled: untrackableOrder,
        },
        {
          value: I18n.t('v2.bookingsPage.cancelBooking'),
          icon: <img src={cancelIcon} alt="" />,
          onClick: () => startCancellation([order], false),
          disabled: !orderCancellable,
          tooltip: !orderCancellable
            ? { placement: 'bottomRight', text: <TooltipContent /> }
            : null,
        },
      ];
    },
    [order],
  );

  return (
    <div>
      <Dropdown
        items={dropdownItems}
        placement={'bottomLeft'}
        className={commonTable.actionDropdown}
        childrenWrapClassName={commonTable.triggerWrapClassName}
        onVisible={trackAnalytics}
      >
        <img src={threeDots} alt="" />
      </Dropdown>
    </div>
  );
};

OrderActionColumn.propTypes = {
  order: PropTypes.shape({}),
  openMSDTrackingModal: PropTypes.func,
  startCancellation: PropTypes.func,
};

OrderActionColumn.defaultProps = {
  order: undefined,
  openMSDTrackingModal: () => {},
  startCancellation: () => {},
};

const OngoingBookingTable = ({ startCancellation }) => {
  const dispatch = useDispatch();

  const [trackMSD, setTrackMSD] = useState([]);
  const userInfo = useSelector(userInfoSelector);
  const { loading } = useSelector(ongoingDeliveriesV2Selector);
  const { pageSize } = useSelector(ongoingBookingFilterSelector);
  const showInTransitQRCode = useSelector(showInTransitQRCodeSelector);

  const ordersToDisplay = useFilteredOngoingOrder();
  const countryCode = useMemo(() => getUserCountryCode(userInfo), [userInfo]);

  const openMSDTrackingModal = links => setTrackMSD(links);
  const closeMSDTrackingModal = () => setTrackMSD([]);

  const onSelectOrder = useCallback(
    (_, selectedOrders) => {
      dispatch(
        NewHistoryDuck.creators.updateOngoingBookingSelection(selectedOrders),
      );
    },
    [dispatch],
  );

  useEffect(
    () => () => {
      onSelectOrder(undefined, []);
    },
    [onSelectOrder],
  );

  const columns = useMemo(
    () => [
      {
        title: I18n.t('v2.orderTable.column.bookingID'),
        dataIndex: 'steps',
        key: 'bookingCode',
        // eslint-disable-next-line
        render: (steps, order) => <BookingCode order={order} />,
        width: '15%',
      },
      {
        title: I18n.t('v2.orderTable.column.sender'),
        dataIndex: 'steps',
        key: 'sender',
        // eslint-disable-next-line
        render: (steps, order) => (
          <DeliveryStep isSender steps={steps} order={order} />
        ),
        width: '10%',
      },
      {
        title: I18n.t('v2.orderTable.column.recipient'),
        dataIndex: 'steps',
        key: 'recipient',
        // eslint-disable-next-line
        render: (steps, order) => <DeliveryStep steps={steps} order={order} />,
        width: '10%',
      },
      {
        title: I18n.t('v2.orderTable.column.pickUpTime'),
        dataIndex: 'scheduledPickupSlot',
        key: 'scheduledPickupSlot',
        sorter: pickUpTimeSorter,
        defaultSortOrder: 'ascend',
        // eslint-disable-next-line
        render: (scheduledPickupSlot, order) => (
          <PickupTime
            scheduledPickupSlot={scheduledPickupSlot}
            order={order}
            countryCode={countryCode}
          />
        ),
        width: '15%',
      },
      {
        title: I18n.t('v2.orderTable.column.driver'),
        dataIndex: 'driver',
        key: 'driver',
        // eslint-disable-next-line
        render: (driver, order) => (
          <Driver
            driver={driver}
            order={order}
            showInTransitQRCode={showInTransitQRCode}
          />
        ),
        width: '15%',
      },
      {
        title: I18n.t('v2.orderTable.column.price'),
        dataIndex: 'amount',
        key: 'price',
        render: (amount, order) =>
          order.currency
            ? getAmountDisplay(amount, order.currency, false)
            : '-',
        width: '10%',
      },
      {
        title: I18n.t('v2.orderTable.column.orderState'),
        key: 'status',
        // eslint-disable-next-line
        render: order => <OrderStatus order={order} />,
        width: '20%',
      },
      {
        title: '',
        key: 'action',
        width: '5%',
        // eslint-disable-next-line
        render: order => (
          <OrderActionColumn
            order={order}
            openMSDTrackingModal={openMSDTrackingModal}
            startCancellation={startCancellation}
          />
        ),
      },
    ],
    [showInTransitQRCode],
  );

  return (
    <Fragment>
      <Table
        columns={columns}
        rowSelection={{ onChange: onSelectOrder }}
        dataSource={ordersToDisplay}
        className={cx(commonTable.table, styles.ongoingBookingTable)}
        pagination={{ pageSize: Number(pageSize) }}
        loading={loading}
        rowKey={getDeliveryID}
      />
      <MSDTrackingModal
        visible={trackMSD.length > 0}
        trackingURLsMap={trackMSD}
        handleClose={closeMSDTrackingModal}
      />
    </Fragment>
  );
};

OngoingBookingTable.propTypes = {
  startCancellation: PropTypes.func,
};

OngoingBookingTable.defaultProps = {
  startCancellation: () => {},
};

export default OngoingBookingTable;
